// Today, the hub's home (public.dashboard_today, rnrinfra migration 20261001144854_hub_today): the work waiting now,
// for the clients picked, with each client's account manager. A row marked done is done for everyone; a quiet client
// or an unsold website comes back after 7 days.

type Who={business_id:string;client:string;manager:string|null};
export type NoReach=Who&{ref:string;lead_id:number;record_number:number;at:string;kind:'call'|'form'};
export type MissedCall=Who&{ref:string;lead_id:number|null;record_number:number|null;at:string;status:string;seconds:number|null;first_time:boolean|null};
export type FailingPhone=Who&{ref:string;last4:string;failures:number;since:string;last_failed_at:string};
export type QuietClient=Who&{ref:string;reason:'no_leads'|'falling';leads_30d:number;prev_30d:number;last_lead_at:string|null};
export type UnsoldSite={ref:string;website:string;leads_7d:number;leads_30d:number;status:string;last_client:string|null;last_lead_at:string|null};
export type TodayAlert={key:string;level:'critical'|'warning'|'info'|string;area:string;title:string;detail:string;since:string};
type List<T>={total:number;rows:T[]};
export type TodayData={
 generated_at:string;picked:boolean;clients:number;
 no_reach:List<NoReach>;missed_calls:List<MissedCall>;failing_phones:List<FailingPhone>;quiet_clients:List<QuietClient>;
 unsold_sites:List<UnsoldSite>|null;alerts:TodayAlert[]|null;done_today:number;
};
export type DoneKind='no_reach'|'missed_call'|'failing_phone'|'quiet_client'|'unsold_site';
export type SectionKey='no_reach'|'missed_calls'|'failing_phones'|'quiet_clients'|'unsold_sites';
export const sectionKind:Record<SectionKey,DoneKind>={no_reach:'no_reach',missed_calls:'missed_call',failing_phones:'failing_phone',quiet_clients:'quiet_client',unsold_sites:'unsold_site'};
/** Rows that come back after a week rather than going for good. */
export const snoozes=(kind:DoneKind)=>kind==='quiet_client'||kind==='unsold_site';

const ET='America/New_York';
/** "Just now", "35 min ago", "5 h ago", then "Sep 24, 4:12 PM" (Eastern). */
export function when(at:string|null,now=Date.now()):string{
 if(!at)return 'Never';
 const t=Date.parse(at);if(Number.isNaN(t))return '—';
 const min=Math.round((now-t)/60_000);
 if(min<1)return 'Just now';
 if(min<60)return `${min} min ago`;
 if(min<24*60)return `${Math.round(min/60)} h ago`;
 return new Intl.DateTimeFormat('en-US',{timeZone:ET,month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(t));
}
/** "Sep 24" in Eastern time; "a few days" for a missing date. */
export function day(at:string|null):string{
 if(!at)return '—';
 const t=Date.parse(at);return Number.isNaN(t)?'—':new Intl.DateTimeFormat('en-US',{timeZone:ET,month:'short',day:'numeric'}).format(new Date(t));
}

export const callStatus:Record<string,string>={missed:'Missed',voicemail:'Voicemail',abandoned:'Hung up'};
export function missedLabel(c:MissedCall):string{
 const parts=[callStatus[c.status]??c.status];
 if(c.seconds)parts.push(c.seconds<60?`${c.seconds}s`:`${Math.floor(c.seconds/60)}m ${c.seconds%60}s`);
 if(c.first_time)parts.push('first-time caller');
 return parts.join(' · ');
}
export function quietLabel(q:QuietClient):string{
 if(q.reason==='no_leads')return q.last_lead_at?`No lead since ${day(q.last_lead_at)}`:'No lead in 60 days';
 return `${q.leads_30d} ${q.leads_30d===1?'lead':'leads'} in 30 days, down from ${q.prev_30d}`;
}
export const unsoldStatus:Record<string,string>={never_sold:'Never sold',client_left:'Client left',client_paused:'Client paused'};

/** Link parameters that open one lead on the Leads page (the search finds it by record number and opens it alone). */
export const leadParams=(record:number)=>({db_workspace:'interactions',db_search:`#${record}`,db_open:'1'});

/** Everything that needs doing, as counts for the tiles at the top. */
export function counts(d:TodayData){
 return {
  no_reach:d.no_reach.total,missed_calls:d.missed_calls.total,failing_phones:d.failing_phones.total,
  quiet_clients:d.quiet_clients.total,unsold_sites:d.unsold_sites?.total??null,
  alerts:d.alerts?d.alerts.length:null,critical:d.alerts?d.alerts.filter(a=>a.level==='critical').length:0,
 };
}
/** True when nothing anywhere needs doing. */
export function allClear(d:TodayData):boolean{
 const c=counts(d);
 return !c.no_reach&&!c.missed_calls&&!c.failing_phones&&!c.quiet_clients&&!c.unsold_sites&&!c.alerts;
}

/** Takes a row out of its section (after Done), keeping the total in step. */
export function withoutRow(d:TodayData,section:SectionKey,ref:string):TodayData{
 const list=d[section];
 if(!list)return d;
 const rows=(list.rows as {ref:string}[]).filter(r=>r.ref!==ref);
 if(rows.length===list.rows.length)return d;
 return {...d,[section]:{total:Math.max(0,list.total-1),rows},done_today:d.done_today+1};
}
/** Puts a row back (after Undo) where it was. */
export function withRow<T extends {ref:string}>(d:TodayData,section:SectionKey,row:T,index:number):TodayData{
 const list=d[section] as List<T>|null;
 if(!list||list.rows.some(r=>r.ref===row.ref))return d;
 const rows=[...list.rows];rows.splice(Math.min(index,rows.length),0,row);
 return {...d,[section]:{total:list.total+1,rows},done_today:Math.max(0,d.done_today-1)};
}
